import { ref, computed } from 'vue';
import { toTypedSchema } from '@vee-validate/zod';
import { useRouter } from 'vue-router';
import { useToast } from 'primevue/usetoast';
import { authService } from '../utils/api/authService';
import type { LoginProps } from '../utils/types';
import { loginFormSchema } from '../utils/schemas';
import { useAppForm } from '../composables/useAppForm';

export function useLogin() {
  const toast = useToast();
  const router = useRouter();

  const isPending = ref(false);

  const signInSchema = computed(() => toTypedSchema(loginFormSchema));

  const { form, handleSubmit, errors, resetForm } = useAppForm({
    fields: ['email', 'password'],
    validationSchema: signInSchema,
  });

  async function login(payload: LoginProps) {
    isPending.value = true;
    try {
      await authService.login(payload);

      router.push('/');
    } catch (error: any) {
      if (error.status === 401 || error.status === 422) {
        toast.add({
          severity: 'error',
          summary: 'Zły email lub hasło',
          life: 3000,
        });
        return;
      }

      toast.add({
        severity: 'error',
        summary: 'Wystąpił błąd podczas logowania',
        life: 3000,
      });
    } finally {
      isPending.value = false;
    }
  }

  const handleLogin = handleSubmit(() => {
    if (Object.keys(errors.value).length > 0) return;

    login({
      email: form.email,
      password: form.password,
    });
  });

  return {
    login,
    isPending,
    form,
    handleLogin,
    errors,
    resetForm,
  };
}
